// Conciliación Bancaria — Banco de Chile (Iter 349)
// Importa cartolas (.xls del portal = CSV con ;), matching automático y conciliación manual.
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { PagosClientesTab } from './PagosClientesTab';
import { CruceAlegraTab } from './CruceAlegraTab';
import { CoberturaTab } from './CoberturaTab';
import { RemuneracionesTab } from './RemuneracionesTab';
import { IngresoExpressDialog } from './IngresoExpressDialog';
import { FactoringDialog } from './FactoringDialog';
import { FactoringTab } from './FactoringTab';
import { CartolasTab } from './CartolasTab';
import { ConciliarMovimientoDialog } from './ConciliarMovimientoDialog';
import { logoBanco } from '../../lib/bankLogos';
import {
  Landmark, Upload, Loader2, CheckCircle2, CircleDashed, EyeOff, Link2,
  RotateCcw, Trash2, RefreshCw, Sparkles, AlertTriangle, Zap, Undo2, HandCoins, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const CLP = (v) => (v || 0).toLocaleString('es-CL');
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const labelMes = (ym) => `${MESES[parseInt(ym.slice(5, 7), 10) - 1]} ${ym.slice(0, 4)}`;
const mesesDeCargas = (cargas) => {
  const set = new Set();
  cargas.forEach((c) => {
    if (!c.fecha_desde || !c.fecha_hasta) return;
    let [y, m] = c.fecha_desde.slice(0, 7).split('-').map(Number);
    const fin = c.fecha_hasta.slice(0, 7);
    for (let i = 0; i < 36; i += 1) {
      const ym = `${y}-${String(m).padStart(2, '0')}`;
      set.add(ym);
      if (ym >= fin) break;
      m += 1;
      if (m > 12) { m = 1; y += 1; }
    }
  });
  return [...set].sort().reverse();
};
const TIPO_LABELS = {
  nomina_cxp: 'Nómina CxP', remuneraciones: 'Remuneraciones', factura_venta: 'Factura venta',
  factura_proveedor: 'Factura proveedor', previsionales: 'Previsionales', impuestos: 'Impuestos',
  gasto_bancario: 'Gasto bancario', gasto_financiero: 'Gasto financiero', factoring: 'Factoring',
  anticipo: 'Anticipo', egreso: 'Egreso', ingreso: 'Ingreso', traspaso_interno: 'Traspaso interno', egreso_alegra: 'Egreso Alegra', cobro_alegra: 'Ingreso Alegra',
  ingreso_alegra: 'Ingreso Alegra', pago_duplicado_cliente: 'Pago duplicado / a favor cliente', otro: 'Otro',
};
const PRIO_AUTO_LABELS = { auto_tef: 'auto · nómina TEF', auto_beneficiario: 'auto · beneficiario + código', auto_codigo_detalle: 'auto · código + detalle' };
const ESTADO_BADGE = {
  pendiente: { cls: 'bg-amber-100 text-amber-700', label: 'Pendiente', Icon: CircleDashed },
  conciliado_auto: { cls: 'bg-emerald-100 text-emerald-700', label: 'Auto', Icon: Sparkles },
  conciliado_manual: { cls: 'bg-blue-100 text-blue-700', label: 'Manual', Icon: CheckCircle2 },
  ignorado: { cls: 'bg-gray-100 text-gray-500', label: 'Ignorado', Icon: EyeOff },
};

export default function ConciliacionBancaria() {
  const [cuentas, setCuentas] = useState([]);
  const [cuentaSel, setCuentaSel] = useState(() => localStorage.getItem('concil_cuenta') || 'todas');
  const [cargas, setCargas] = useState([]);
  const [cargaSel, setCargaSel] = useState('todas');
  const [mesSel, setMesSel] = useState('todos');
  const [tab, setTab] = useState('movimientos');
  const [movs, setMovs] = useState([]);
  const [totalMovs, setTotalMovs] = useState(0);
  const [resumen, setResumen] = useState(null);
  const [estadoFiltro, setEstadoFiltro] = useState('todos');
  const [tipoFiltro, setTipoFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [rematching, setRematching] = useState(false);
  const fileRef = useRef(null);

  const [dialog, setDialog] = useState({ open: false, mov: null });
  const [grupoDialog, setGrupoDialog] = useState(null); // Iter 380 — lista de movimientos para conciliar en grupo N:M
  const [selMovs, setSelMovs] = useState([]);
  const [ingresoDlg, setIngresoDlg] = useState({ open: false, mov: null });
  const [factoringDlg, setFactoringDlg] = useState({ open: false, mov: null });

  const movsSel = movs.filter((m) => selMovs.includes(m.id));
  const totalSel = movsSel.reduce((t, m) => t + Math.round(m.cargo || m.abono || 0), 0);
  const pendientesVisibles = movs.filter((m) => m.estado === 'pendiente');
  const toggleSel = (m) => setSelMovs((prev) => {
    if (prev.includes(m.id)) return prev.filter((x) => x !== m.id);
    const primero = movs.find((x) => x.id === prev[0]);
    if (primero && ((primero.abono > 0) !== (m.abono > 0))) { toast.error('No se pueden mezclar abonos y cargos en el mismo grupo'); return prev; }
    return [...prev, m.id];
  });
  const toggleTodos = () => setSelMovs((prev) => {
    if (prev.length && pendientesVisibles.every((m) => prev.includes(m.id))) return [];
    const abonos = pendientesVisibles.filter((m) => m.abono > 0);
    const cargos = pendientesVisibles.filter((m) => m.cargo > 0);
    if (abonos.length && cargos.length) { toast.info(`Se seleccionan solo los ${abonos.length >= cargos.length ? 'abonos' : 'cargos'} visibles (no se mezclan con ${abonos.length >= cargos.length ? 'cargos' : 'abonos'})`); }
    return (abonos.length >= cargos.length ? abonos : cargos).map((m) => m.id);
  });

  const cuentaParam = cuentaSel !== 'todas' ? { cuenta: cuentaSel } : {};
  const bancoActual = cuentas.find((c) => c.cuenta === cuentaSel) || null;
  const labelCuenta = (c) => `${c.nombre || c.titular || 'Cuenta'} · ${c.cuenta}`;

  const loadCuentas = useCallback(async () => {
    const r = await api.get('/conciliacion-bancaria/cuentas');
    setCuentas(r.data.cuentas || []);
  }, []);

  const loadCargas = useCallback(async () => {
    const r = await api.get('/conciliacion-bancaria/cargas', { params: cuentaSel !== 'todas' ? { cuenta: cuentaSel } : {} });
    setCargas(r.data.cargas || []);
  }, [cuentaSel]);

  const loadMovs = useCallback(async () => {
    const params = { orden: 'desc' };
    if (cuentaSel !== 'todas') params.cuenta = cuentaSel;
    if (cargaSel !== 'todas') params.carga_id = cargaSel;
    if (mesSel !== 'todos') params.mes = mesSel;
    if (estadoFiltro === 'destacados') params.destacado = true;
    else if (estadoFiltro === 'revision_manual') { params.estado = 'pendiente'; params.revision_manual = true; }
    else if (estadoFiltro !== 'todos') params.estado = estadoFiltro;
    if (tipoFiltro !== 'todos') params.tipo = tipoFiltro;
    if (busqueda.trim()) params.q = busqueda.trim();
    const resumenParams = { ...(cuentaSel !== 'todas' ? { cuenta: cuentaSel } : {}), ...(cargaSel !== 'todas' ? { carga_id: cargaSel } : {}), ...(mesSel !== 'todos' ? { mes: mesSel } : {}) };
    const [rm, rr] = await Promise.all([
      api.get('/conciliacion-bancaria/movimientos', { params }),
      api.get('/conciliacion-bancaria/resumen', { params: resumenParams }),
    ]);
    setMovs(rm.data.movimientos || []);
    setTotalMovs(rm.data.total ?? (rm.data.movimientos || []).length);
    setResumen(rr.data);
  }, [cuentaSel, cargaSel, mesSel, estadoFiltro, tipoFiltro, busqueda]);

  useEffect(() => { loadCuentas().catch(() => toast.error('Error cargando cuentas bancarias')); }, [loadCuentas]);
  useEffect(() => { loadCargas().catch(() => toast.error('Error cargando cartolas')); }, [loadCargas]);
  useEffect(() => { loadMovs().catch(() => {}); }, [loadMovs]);

  const cambiarCuenta = (v) => {
    setCuentaSel(v); setCargaSel('todas'); setMesSel('todos');
    localStorage.setItem('concil_cuenta', v);
  };

  const cargaProcesando = cargas.find((c) => c.estado_match === 'procesando');
  useEffect(() => {
    if (!cargaProcesando) return undefined;
    const t = setInterval(() => { loadCargas().catch(() => {}); loadMovs().catch(() => {}); }, 3000);
    return () => clearInterval(t);
  }, [cargaProcesando, loadCargas, loadMovs]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/conciliacion-bancaria/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000, params: cuentaParam });
      toast.success(r.data.message);
      await Promise.all([loadCargas(), loadCuentas()]);
      setCargaSel(r.data.carga.id);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (err.code === 'ECONNABORTED' ? 'El servidor tardó demasiado. Revisa la lista de cartolas antes de reintentar.' : 'Error subiendo la cartola'));
    } finally {
      setSubiendo(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRematch = async () => {
    setRematching(true);
    try {
      const params = { ...cuentaParam, ...(cargaSel !== 'todas' ? { carga_id: cargaSel } : {}) };
      const r = await api.post('/conciliacion-bancaria/rematch', null, { params });
      const g = (r.data.grupos_tef || 0) + (r.data.grupos_beneficiario || 0) + (r.data.grupos_codigo_detalle || 0);
      toast.success(`Re-match: ${r.data.auto_conciliados} de ${r.data.revisados} pendientes conciliados${g ? ` · ${g} grupo(s) automático(s)` : ''}`);
      loadMovs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en re-match');
    } finally { setRematching(false); }
  };

  const handleEliminarCarga = async (id = cargaSel) => {
    if (!id || id === 'todas') return;
    const c = cargas.find((x) => x.id === id);
    if (!window.confirm(`¿Eliminar la carga "${c?.nombre_archivo}" y todos sus movimientos?`)) return;
    const done = () => { toast.success('Carga eliminada'); setCargaSel('todas'); loadCargas(); loadMovs(); };
    try {
      await api.delete(`/conciliacion-bancaria/cargas/${id}`);
      done();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'carga_con_conciliados') {
        if (!window.confirm(`⚠️ Esta carga tiene ${d.trabajados} movimientos ya conciliados o ignorados. Si la eliminas se pierde ese trabajo (los pagos ya registrados en Alegra/CxC NO se revierten).\n\n¿Eliminar de todas formas?`)) return;
        try {
          await api.delete(`/conciliacion-bancaria/cargas/${id}`, { params: { force: true } });
          done();
        } catch (e2) { toast.error(e2.response?.data?.detail || 'Error eliminando'); }
        return;
      }
      toast.error(typeof d === 'string' ? d : 'Error eliminando');
    }
  };

  const verCarga = (id) => { setCargaSel(id); setMesSel('todos'); setTab('movimientos'); };

  const openConciliar = (mov) => setDialog({ open: true, mov });

  const handleDeshacerVB = async (mov) => {
    try {
      const dry = await api.post(`/conciliacion-bancaria/pagos-clientes/${mov.id}/deshacer-vb`, { motivo: '' }, { params: { dry_run: true } });
      const p = dry.data.plan;
      const lineas = [`Deshacer VB de: ${p.ref_desc}`, ''];
      if (p.tipo === 'cobro_alegra' || p.tipo === 'ingreso') lineas.push(`• Se desvincula el Recibo de Caja (id Alegra ${p.cobro_desvinculado}) — NO se elimina, sigue en Alegra y vuelve a quedar disponible.`);
      p.facturas.forEach((f) => lineas.push(f.eliminar_pago
        ? `• Factura ${f.numero}: se ELIMINA el cobro #${f.alegra_payment_id} en Alegra y la CxC vuelve a pendiente.`
        : `• Factura ${f.numero}: el cobro #${f.alegra_payment_id || '—'} NO fue creado por este VB, se mantiene.`));
      lineas.push('', 'El depósito vuelve a PENDIENTE. Escribe el motivo:');
      const motivo = window.prompt(lineas.join('\n'), '');
      if (motivo === null) return;
      if (motivo.trim().length < 5) { toast.error('Indica el motivo (mín. 5 caracteres)'); return; }
      const r = await api.post(`/conciliacion-bancaria/pagos-clientes/${mov.id}/deshacer-vb`, { motivo });
      if (r.data.errores?.length) toast.warning(`${r.data.message}: ${r.data.errores.join(' | ')}`);
      else toast.success(r.data.message);
      loadMovs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error deshaciendo el VB'); }
  };

  const handleIgnorar = async (mov) => {
    try {
      await api.post(`/conciliacion-bancaria/movimientos/${mov.id}/ignorar`, { motivo: '' });
      loadMovs();
    } catch { toast.error('Error'); }
  };

  const handlePendiente = async (mov) => {
    if (mov.vb_pago_cliente) { handleDeshacerVB(mov); return; }
    if (mov.match?.grupo_id && !window.confirm(`Este movimiento está conciliado en grupo con otros ${(mov.match.grupo_mov_ids?.length || 1) - 1} movimiento(s). Volver a pendiente deshace el grupo completo. ¿Continuar?`)) return;
    try {
      const r = await api.post(`/conciliacion-bancaria/movimientos/${mov.id}/pendiente`);
      if (r.data?.message) toast.success(r.data.message);
      loadMovs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4 p-6" data-testid="conciliacion-bancaria-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {logoBanco(bancoActual) ? (
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border bg-white p-1 shadow-sm">
              <img src={logoBanco(bancoActual)} alt={bancoActual?.nombre || 'Banco'} className="h-full w-full object-contain" data-testid="header-banco-logo" />
            </div>
          ) : (
            <div className="rounded-xl bg-sky-100 p-2"><Landmark className="h-6 w-6 text-sky-700" /></div>
          )}
          <div>
            <h1 className="text-2xl font-bold">Conciliación Bancaria</h1>
            <p className="text-sm text-muted-foreground" data-testid="subtitulo-cuenta">
              {bancoActual ? `${bancoActual.nombre || bancoActual.titular || 'Cuenta'} · cta ${bancoActual.cuenta}` : 'Todas las cuentas bancarias'} — sube la cartola (.xls del portal) y concilia cargos y abonos
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={cuentaSel} onValueChange={cambiarCuenta}>
            <SelectTrigger className="w-72" data-testid="select-cuenta-banco"><Landmark className="mr-2 h-4 w-4 shrink-0 text-sky-700" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas" data-testid="cuenta-opcion-todas">Todas las cuentas</SelectItem>
              {cuentas.map((c) => (
                <SelectItem key={c.cuenta} value={c.cuenta} data-testid={`cuenta-opcion-${c.cuenta}`}>
                  <span className="flex items-center gap-2">
                    {logoBanco(c) && <img src={logoBanco(c)} alt="" className="h-4 w-4 shrink-0 object-contain" data-testid={`cuenta-logo-${c.cuenta}`} />}
                    <span>{labelCuenta(c)}{c.pendientes ? ` · ${c.pendientes} pend.` : ''}{!c.configurada ? ' · sin configurar' : ''}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input ref={fileRef} type="file" accept=".xls,.xlsx,.csv,.txt" className="hidden" onChange={handleUpload} data-testid="input-cartola-file" />
          <Button onClick={() => fileRef.current?.click()} disabled={subiendo} data-testid="btn-subir-cartola">
            {subiendo ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
            Subir cartola
          </Button>
          <Button variant="outline" onClick={handleRematch} disabled={rematching} data-testid="btn-rematch">
            {rematching ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
            Re-match
          </Button>
        </div>
      </div>

      {cargaProcesando && (
        <div className="flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm text-sky-800" data-testid="banner-match-progreso">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>
            Conciliando automáticamente <b>{cargaProcesando.nombre_archivo}</b>: {cargaProcesando.match_revisados || 0}/{cargaProcesando.match_total || 0} movimientos revisados · {cargaProcesando.auto_conciliados || 0} conciliados
          </span>
        </div>
      )}

      {resumen && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6" data-testid="resumen-cards">
          {[
            ['Movimientos', resumen.total_movimientos, ''],
            ['Conciliados', resumen.conciliados, 'text-emerald-600'],
            ['Pendientes', resumen.pendientes, 'text-amber-600'],
            ['Revisión manual', resumen.revision_manual ?? 0, 'text-orange-600'],
            ['Cargos', `$${CLP(resumen.total_cargos)}`, 'text-red-600'],
            ['Abonos', `$${CLP(resumen.total_abonos)}`, 'text-emerald-600'],
          ].map(([label, val, cls]) => (
            <Card key={label} className={label === 'Revisión manual' ? 'cursor-pointer hover:shadow-md' : ''} onClick={label === 'Revisión manual' ? () => setEstadoFiltro('revision_manual') : undefined} data-testid={label === 'Revisión manual' ? 'card-revision-manual' : undefined}><CardContent className="p-3">
              <p className="text-[11px] uppercase text-muted-foreground">{label}</p>
              <p className={`text-lg font-bold ${cls}`}>{val}</p>
              {label === 'Conciliados' && <p className="text-[11px] text-muted-foreground">{resumen.pct_conciliado}% resuelto</p>}
              {label === 'Revisión manual' && <p className="text-[11px] text-muted-foreground">egreso/ingreso con mismo monto (P3)</p>}
            </CardContent></Card>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="conciliacion-tabs">
          <TabsTrigger value="movimientos" data-testid="tab-movimientos">Movimientos</TabsTrigger>
          <TabsTrigger value="cartolas" data-testid="tab-cartolas">Cartolas ({cargas.length})</TabsTrigger>
          <TabsTrigger value="pagos-clientes" data-testid="tab-pagos-clientes">Pagos de Clientes (VB)</TabsTrigger>
          <TabsTrigger value="cruce-alegra" data-testid="tab-cruce-alegra">Cruce Alegra</TabsTrigger>
          <TabsTrigger value="remuneraciones" data-testid="tab-remuneraciones">Remuneraciones</TabsTrigger>
          <TabsTrigger value="factoring" data-testid="tab-factoring">Factoring</TabsTrigger>
          <TabsTrigger value="cobertura" data-testid="tab-cobertura">Cobertura</TabsTrigger>
        </TabsList>

        <TabsContent value="movimientos" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={cargaSel} onValueChange={setCargaSel}>
          <SelectTrigger className="w-64" data-testid="select-carga"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las cartolas</SelectItem>
            {cargas.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.nombre_archivo} ({fmtFecha(c.fecha_desde)} → {fmtFecha(c.fecha_hasta)})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={mesSel} onValueChange={setMesSel}>
          <SelectTrigger className="w-36" data-testid="select-mes"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los meses</SelectItem>
            {mesesDeCargas(cargas).map((ym) => (
              <SelectItem key={ym} value={ym}>{labelMes(ym)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={estadoFiltro} onValueChange={setEstadoFiltro}>
          <SelectTrigger className="w-44" data-testid="select-estado"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            <SelectItem value="pendiente">Pendientes</SelectItem>
            <SelectItem value="revision_manual">Revisión manual (P3)</SelectItem>
            <SelectItem value="conciliado_auto">Conciliados auto</SelectItem>
            <SelectItem value="conciliado_manual">Conciliados manual</SelectItem>
            <SelectItem value="ignorado">Ignorados</SelectItem>
            <SelectItem value="destacados">⚠ Destacados</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tipoFiltro} onValueChange={setTipoFiltro}>
          <SelectTrigger className="w-36" data-testid="select-tipo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Cargos y abonos</SelectItem>
            <SelectItem value="cargo">Solo cargos</SelectItem>
            <SelectItem value="abono">Solo abonos</SelectItem>
          </SelectContent>
        </Select>
        <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar en detalle..."
          className="w-56" data-testid="input-busqueda" />
        {cargaSel !== 'todas' && (
          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleEliminarCarga(cargaSel)} data-testid="btn-eliminar-carga">
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Eliminar carga
          </Button>
        )}
        <span className="ml-auto text-xs text-muted-foreground" data-testid="movs-count">
          {movs.length < totalMovs ? `Mostrando ${movs.length} de ${totalMovs}` : `${totalMovs} movimientos`} · más recientes primero
        </span>
      </div>

      <Card>
        {selMovs.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 border-b bg-sky-50 px-4 py-2 text-sm" data-testid="barra-seleccion-grupo">
            <span className="font-medium text-sky-900">{selMovs.length} movimiento{selMovs.length === 1 ? '' : 's'} seleccionado{selMovs.length === 1 ? '' : 's'}</span>
            <span className="font-mono text-sky-900" data-testid="total-seleccion-grupo">${CLP(totalSel)}</span>
            <span className="text-xs text-muted-foreground">Concilia varios depósitos (o cargos) con uno o más documentos de Alegra cuya suma sea igual.</span>
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setSelMovs([])} data-testid="btn-limpiar-seleccion">Limpiar</Button>
              <Button size="sm" onClick={() => setGrupoDialog(movsSel)} disabled={selMovs.length < 2} data-testid="btn-conciliar-grupo">
                <Link2 className="mr-1 h-3.5 w-3.5" /> Conciliar {selMovs.length} en grupo
              </Button>
            </div>
          </div>
        )}
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50 text-left text-xs text-muted-foreground">
                <th className="w-8 px-3 py-2">
                  {pendientesVisibles.length > 0 && (
                    <input type="checkbox" className="cursor-pointer" title="Seleccionar todos los pendientes visibles (del mismo tipo)" onChange={toggleTodos}
                      checked={selMovs.length > 0 && pendientesVisibles.every((m) => selMovs.includes(m.id))} data-testid="check-todos-pendientes" />
                  )}
                </th>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Detalle</th>
                <th className="px-3 py-2 text-right">Cargo</th>
                <th className="px-3 py-2 text-right">Abono</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Conciliado con</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {movs.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground" data-testid="movs-empty">
                  {cargas.length === 0 ? (bancoActual ? `Sin cartolas para ${labelCuenta(bancoActual)}. Sube la primera cartola de esta cuenta` : 'Sube tu primera cartola para comenzar') : 'Sin movimientos con estos filtros'}
                </td></tr>
              )}
              {movs.map((m) => {
                const eb = ESTADO_BADGE[m.estado] || ESTADO_BADGE.pendiente;
                return (
                  <tr key={m.id} className={`border-b last:border-0 hover:bg-slate-50/50 ${m.destacado ? 'bg-amber-50 hover:bg-amber-100/60' : ''} ${selMovs.includes(m.id) ? 'bg-sky-50' : ''}`} data-testid={`mov-row-${m.id}`}>
                    <td className="px-3 py-2">
                      {m.estado === 'pendiente' && (
                        <input type="checkbox" className="cursor-pointer" checked={selMovs.includes(m.id)} onChange={() => toggleSel(m)} data-testid={`check-mov-${m.id}`} />
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtFecha(m.fecha)}</td>
                    <td className="max-w-[320px] truncate px-3 py-2" title={m.detalle}>
                      {m.destacado && <AlertTriangle className="mr-1 inline h-3.5 w-3.5 text-amber-600" data-testid={`mov-destacado-${m.id}`} />}
                      {m.detalle}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-xs text-red-600">{m.cargo ? `$${CLP(m.cargo)}` : ''}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-xs text-emerald-600">{m.abono ? `$${CLP(m.abono)}` : ''}</td>
                    <td className="px-3 py-2">
                      <Badge className={`${eb.cls} hover:${eb.cls} gap-1`}><eb.Icon className="h-3 w-3" />{eb.label}</Badge>
                      {m.destacado && <Badge className="ml-1 gap-1 bg-amber-200 text-amber-900 hover:bg-amber-200" title={m.destacado_motivo} data-testid={`badge-destacado-${m.id}`}><AlertTriangle className="h-3 w-3" />Destacado</Badge>}
                      {m.estado === 'pendiente' && m.revision_manual && <Badge className="ml-1 gap-1 bg-orange-100 text-orange-800 hover:bg-orange-100" title={m.sugerencia?.ref_desc} data-testid={`badge-revision-${m.id}`}><Search className="h-3 w-3" />Revisión manual</Badge>}
                    </td>
                    <td className="max-w-[280px] truncate px-3 py-2 text-xs text-muted-foreground" title={`${m.match?.ref_desc || m.sugerencia?.ref_desc || ''}${m.destacado_motivo ? `\n⚠ ${m.destacado_motivo}` : ''}`}>
                      {m.match ? `${TIPO_LABELS[m.match.tipo] || m.match.tipo}${Number.isInteger(m.match.prioridad) ? ` (P${m.match.prioridad})` : (PRIO_AUTO_LABELS[m.match.prioridad] ? ` (${PRIO_AUTO_LABELS[m.match.prioridad]})` : '')}${m.match.ref_desc ? ` · ${m.match.ref_desc}` : ''}` : (m.sugerencia ? <span className="text-orange-700">Sugerido: {m.sugerencia.ref_desc}</span> : '—')}
                      {m.destacado_motivo && <span className="block truncate text-amber-700">⚠ {m.destacado_motivo}</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {m.estado === 'pendiente' ? (
                        <>
                          <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => openConciliar(m)} data-testid={`btn-conciliar-${m.id}`}>
                            <Link2 className="mr-1 h-3.5 w-3.5" /> Conciliar
                          </Button>
                          {m.abono > 0 && (
                            <>
                              <Button variant="ghost" size="sm" className="text-sky-600" onClick={() => setIngresoDlg({ open: true, mov: m })} title="Ingreso exprés en Alegra" data-testid={`btn-ingreso-express-${m.id}`}>
                                <Zap className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-violet-600" onClick={() => setFactoringDlg({ open: true, mov: m })} title="Conciliar como factoring / financiamiento" data-testid={`btn-factoring-${m.id}`}>
                                <HandCoins className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" className="text-gray-400" onClick={() => handleIgnorar(m)} title="Ignorar" data-testid={`btn-ignorar-${m.id}`}>
                            <EyeOff className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : m.vb_pago_cliente ? (
                        <Button variant="ghost" size="sm" className="text-orange-600" onClick={() => handleDeshacerVB(m)} title="Deshacer VB (revierte Alegra/CxC)" data-testid={`btn-deshacer-vb-${m.id}`}>
                          <Undo2 className="mr-1 h-3.5 w-3.5" /> Deshacer VB
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" className="text-orange-500" onClick={() => handlePendiente(m)} title="Volver a pendiente" data-testid={`btn-pendiente-${m.id}`}>
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="cartolas">
          <CartolasTab cargas={cargas} onVer={verCarga} onEliminar={handleEliminarCarga} />
        </TabsContent>

        <TabsContent value="pagos-clientes">
          <PagosClientesTab cargaSel={cargaSel} cuentaSel={cuentaSel} onChanged={loadMovs} />
        </TabsContent>

        <TabsContent value="cruce-alegra">
          <CruceAlegraTab />
        </TabsContent>

        <TabsContent value="remuneraciones">
          <RemuneracionesTab onChanged={loadMovs} />
        </TabsContent>

        <TabsContent value="factoring">
          <FactoringTab refreshKey={movs.length} onChanged={loadMovs} />
        </TabsContent>

        <TabsContent value="cobertura">
          <CoberturaTab cuenta={cuentaSel !== 'todas' ? cuentaSel : ''} refreshKey={cargas.map((c) => c.id).join(',')} />
        </TabsContent>
      </Tabs>

      <ConciliarMovimientoDialog open={dialog.open} mov={dialog.mov} onClose={() => setDialog({ open: false, mov: null })} onDone={loadMovs} />
      <ConciliarMovimientoDialog open={!!grupoDialog} movs={grupoDialog || []} onClose={() => setGrupoDialog(null)} onDone={() => { setSelMovs([]); loadMovs(); }} />

      <IngresoExpressDialog open={ingresoDlg.open} mov={ingresoDlg.mov} onClose={() => setIngresoDlg({ open: false, mov: null })} onDone={loadMovs} />
      <FactoringDialog open={factoringDlg.open} mov={factoringDlg.mov} onClose={() => setFactoringDlg({ open: false, mov: null })} onDone={loadMovs} />
    </div>
  );
}
